import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { MEDIA } from "../data/media";

const DISCIPLINES = [
  { n: "01", t: "Engineering", d: "Building systems that are robust, maintainable, and genuinely fit for purpose." },
  { n: "02", t: "Quality Management", d: "A discipline for getting things right — repeatably, at scale." },
  { n: "03", t: "Business Operations", d: "Understanding how businesses actually run, day to day." },
  { n: "04", t: "Modern AI-Driven Development", d: "Applying today's AI to move faster and build smarter." },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About — Decades of Engineering, Quality & AI | AberCal"
        description="AberCal blends decades of engineering, quality management and business operations with modern AI-driven development — so the platforms fit how your business actually works."
        path="/about"
      />
      <PageHero
        testid="about-hero"
        overline="Experience"
        title="Decades across"
        accent="four disciplines."
        subtitle="It's why AberCal understands how businesses actually operate — not just how to build the platform behind them."
        bg={MEDIA.spark}
      />

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="text-2xl font-light leading-relaxed text-[var(--text)] sm:text-3xl">
              I spent decades solving engineering problems, managing quality at scale, and running the day-to-day of real businesses — long before AI made building faster.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-[var(--text-2)]">
              That's a rare combination. Modern AI-driven development just lets me deliver it faster than ever.
            </p>
          </Reveal>
          <div className="mt-10 space-y-4 border-l-2 border-[var(--red)] pl-6">
            {[
              "Most developers understand code but not business.",
              "Most business consultants understand business but can't build the technology.",
              "I do both.",
            ].map((line, i) => (
              <Reveal key={line} delay={0.15 + i * 0.08}>
                <p className={`font-head font-light leading-snug ${i === 2 ? "text-3xl text-gold sm:text-4xl" : "text-2xl text-[var(--text)] sm:text-3xl"}`}>{line}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-px overflow-hidden border border-[var(--border)] sm:grid-cols-2">
          {DISCIPLINES.map((d, i) => (
            <Reveal key={d.t} delay={(i % 2) * 0.1}>
              <div className="group h-full bg-[var(--surface)] p-12 transition-colors duration-500 hover:bg-[var(--elevated)]">
                <span className="font-head text-5xl font-light text-[var(--muted)] transition-colors group-hover:text-gold">{d.n}</span>
                <h3 className="mt-6 font-head text-3xl font-light">{d.t}</h3>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-[var(--text-2)]">{d.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden px-6 py-40">
        <img src={MEDIA.forge} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="pointer-events-none absolute inset-0 bg-[var(--bg)]/70" />
        <div className="relative mx-auto max-w-5xl">
          <Reveal>
            <p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">The AberCal thesis</p>
            <p className="font-head text-4xl font-light leading-[1.15] tracking-tight sm:text-6xl">
              Because technology is only valuable when it <span className="text-gold italic">delivers results.</span>
            </p>
            <Link to="/contact" className="btn-gold mt-14 inline-block">Start a Conversation</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
